import { useState, useEffect } from 'react';
import { toastService as toast } from '../../services/toastService';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../contexts/LanguageContext';
import { Check } from 'lucide-react';
import { SignInForm } from './auth/SignInForm';
import { SignUpForm } from './auth/SignUpForm';
import { DEFAULT_LOGO_URL } from '../../utils/Constants';

type Mode = 'login' | 'signup';

export function LoginPage() {
  const { sessionExpired, accountDeactivated } = useAuth();
  const { t } = useLanguage();

  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    // Message dédié pour un accès révoqué / compte désactivé (ex: guest dont le siège
    // a été révoqué) — distinct du « session expirée » générique (V4-AGENCY-GUEST-REVOKE-MSG).
    if (accountDeactivated) {
      toast.warning(t.auth.accountDeactivatedTitle, t.auth.accountDeactivatedDescription);
    } else if (sessionExpired) {
      toast.warning(t.auth.sessionExpiredTitle || 'Session expirée');
    }
  }, [sessionExpired, accountDeactivated, t.auth]);

  useEffect(() => {
    // Redirection en cas d'erreur depuis Stripe
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('stripe_error')) {
      toast.error(t.auth.signupPaymentCancelled);
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Redirection en cas de succès depuis Stripe
    if (urlParams.get('stripe_success')) {
      toast.success(t.auth.signupWelcome);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [t.auth.signupPaymentCancelled, t.auth.signupWelcome]);

  // LANDING_APP_MODE_PARAM (2026-05-27) — Parse `?mode=signup` envoyé depuis
  // la landing (raiso-landing-page Header/Hero/Pricing CTAs) pour pre-select
  // le formulaire d'inscription. `?source=landing` est conservé pour analytics
  // attribution. Les query params sont nettoyés de l'URL après lecture pour
  // éviter qu'un refresh ne re-trigger le mode signup.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const modeParam = urlParams.get('mode');
    if (modeParam === 'signup' || modeParam === 'login') {
      setMode(modeParam as Mode);
      urlParams.delete('mode');
      const remaining = urlParams.toString();
      const newSearch = remaining ? `?${remaining}` : '';
      window.history.replaceState(
        {},
        document.title,
        `${window.location.pathname}${newSearch}${window.location.hash}`
      );
    }
  }, []);

  const switchToMode = (newMode: Mode) => {
    setMode(newMode);
  };

  return (
    <div className="w-full min-h-screen lg:grid lg:grid-cols-2">
      {/* Left Side - Branding & Visuals */}
      <div className="hidden lg:flex flex-col justify-between bg-zinc-900 p-10 text-white relative overflow-hidden h-full">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-[-20%] left-[-20%] w-[80%] h-[80%] rounded-full bg-[var(--color-primary)] opacity-20 blur-[120px]" />
          <div className="absolute bottom-[-20%] right-[-20%] w-[80%] h-[80%] rounded-full bg-blue-600 opacity-20 blur-[120px]" />
          <div className="absolute top-[40%] left-[40%] w-[40%] h-[40%] rounded-full bg-purple-500 opacity-10 blur-[100px]" />
        </div>

        <div className="relative z-10 flex items-center gap-3">
          <div className="bg-white/10 p-2 rounded-xl backdrop-blur-md border border-white/10">
            <img src={DEFAULT_LOGO_URL} alt="Raiso Logo" className="w-8 h-8 object-contain" />
          </div>
          <span className="text-2xl font-bold tracking-tight">Raiso</span>
        </div>

        <div className="relative z-10 max-w-md">
          <h1 className="text-4xl font-bold mb-6 leading-tight">{t.auth.heroTitle}</h1>
          <p className="text-zinc-400 text-lg mb-8">{t.auth.heroSubtitle}</p>
          <div className="space-y-4">
            {t.auth.features.map((feature, i) => (
              <div key={i} className="flex items-center gap-3 text-zinc-300">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[var(--color-primary)]/20 text-[var(--color-primary)]">
                  <Check className="w-3.5 h-3.5" />
                </div>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-sm text-zinc-500">
          &copy; {new Date().getFullYear()} {t.auth.copyright}
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex items-start sm:items-center justify-center p-4 sm:p-8 bg-[var(--color-background)] relative min-h-screen lg:min-h-0 overflow-y-auto">
        <div className="absolute inset-0 z-0 lg:hidden pointer-events-none">
          <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-[var(--color-primary)] opacity-10 blur-[80px]" />
        </div>

        <div className={`w-full mx-auto space-y-6 relative z-10 px-6 ${mode === 'signup' ? 'max-w-3xl' : 'max-w-sm'}`}>
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-2">
            <div className="bg-[var(--color-surface)] p-3 rounded-xl shadow-sm border border-[var(--color-border)]">
              <img src={DEFAULT_LOGO_URL} alt="Raiso Logo" className="w-10 h-10 object-contain" />
            </div>
          </div>

          {/* Header */}
          <div className="flex flex-col space-y-2 text-center lg:text-left">
            <h2 className="text-3xl font-bold tracking-tight text-[var(--color-text-primary)]">
              {mode === 'login' ? t.auth.title : t.auth.signupTitle}
            </h2>
            <p className="text-[var(--color-text-secondary)]">
              {mode === 'login' ? t.auth.subtitle : t.auth.signupSubtitle}
            </p>
          </div>

          {/* Form */}
          {mode === 'login' ? (
            <SignInForm email={email} setEmail={setEmail} password={password} setPassword={setPassword} />
          ) : (
            <SignUpForm email={email} setEmail={setEmail} password={password} setPassword={setPassword} />
          )}

          {/* Toggle login/signup */}
          <div className="text-center text-sm text-[var(--color-text-secondary)]">
            {mode === 'login' ? (
              <>{t.auth.switchToSignup}{' '}<button type="button" onClick={() => switchToMode('signup')} className="text-[var(--color-primary)] font-medium hover:underline cursor-pointer">{t.auth.switchToSignupLink}</button></>
            ) : (
              <>{t.auth.switchToLogin}{' '}<button type="button" onClick={() => switchToMode('login')} className="text-[var(--color-primary)] font-medium hover:underline cursor-pointer">{t.auth.switchToLoginLink}</button></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
